/*
 * HDC Speech-Search (Sprachsuche) — Original HDC implementation.
 * Custom element <hdc-speech-search>. Nutzt die Web Speech API
 * (SpeechRecognition / webkitSpeechRecognition), um das zugehoerige Suchfeld
 * per Sprache zu befuellen.
 *
 * Graceful degradation: Ist die API nicht verfuegbar, bleibt der Button
 * ausgeblendet (das Element startet mit hidden und wird nur bei Support sichtbar).
 * Kein jQuery, keine externen Libraries.
 */
if (!customElements.get('hdc-speech-search')) {
  customElements.define(
    'hdc-speech-search',
    class HdcSpeechSearch extends HTMLElement {
      constructor() {
        super();
        this.onToggle = this.onToggle.bind(this);
        this.listening = false;
      }

      connectedCallback() {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

        this.button = this.querySelector('[data-speech-toggle]');
        this.statusEl = this.querySelector('[data-speech-status]');

        const i18n = this.querySelector('[data-speech-i18n]');
        this.strings = i18n ? { ...i18n.dataset } : {};

        // Kein Support -> Button ausgeblendet lassen, still beenden.
        if (!SpeechRecognition) {
          this.hidden = true;
          return;
        }

        // Ziel-Suchfeld ermitteln.
        this.input =
          (this.dataset.inputId && document.getElementById(this.dataset.inputId)) ||
          this.closest('form') && this.closest('form').querySelector('input[type="search"], input[name="q"]');

        if (!this.input) {
          this.hidden = true;
          return;
        }

        // Support vorhanden -> Element einblenden.
        this.hidden = false;

        this.recognition = new SpeechRecognition();
        this.recognition.lang = this.dataset.lang || 'de-DE';
        this.recognition.interimResults = true;
        this.recognition.continuous = false;
        this.recognition.maxAlternatives = 1;

        this.recognition.onresult = (event) => this.onResult(event);
        this.recognition.onerror = (event) => this.onError(event);
        this.recognition.onend = () => this.onEnd();

        if (this.button) this.button.addEventListener('click', this.onToggle);
      }

      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.onToggle);
        if (this.recognition && this.listening) {
          try {
            this.recognition.abort();
          } catch (error) {
            /* no-op */
          }
        }
      }

      t(key, fallback) {
        return this.strings['t' + key] || fallback || '';
      }

      onToggle() {
        if (this.listening) {
          this.stop();
        } else {
          this.start();
        }
      }

      start() {
        try {
          this.recognition.start();
          this.listening = true;
          this.setActive(true);
          this.setStatus(this.t('Listening', 'Ich hoere zu ...'));
        } catch (error) {
          // start() wirft, wenn bereits laeuft — Zustand robust zuruecksetzen.
          this.listening = false;
          this.setActive(false);
        }
      }

      stop() {
        this.listening = false;
        try {
          this.recognition.stop();
        } catch (error) {
          /* no-op */
        }
        this.setActive(false);
      }

      onResult(event) {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        transcript = transcript.trim();
        if (!transcript) return;

        this.input.value = transcript;

        // Input-Event feuern, damit Dawns Predictive Search reagiert.
        this.input.dispatchEvent(new Event('input', { bubbles: true }));

        // Bei finalem Ergebnis Fokus zurueck ins Feld.
        const last = event.results[event.results.length - 1];
        if (last && last.isFinal) {
          this.input.focus();
        }
      }

      onError(event) {
        this.listening = false;
        this.setActive(false);
        if (event && (event.error === 'not-allowed' || event.error === 'service-not-allowed')) {
          this.setStatus(this.t('Denied', 'Mikrofonzugriff wurde abgelehnt.'));
        } else if (event && event.error === 'no-speech') {
          // Kein Sprachinput — leise beenden.
          this.setStatus('');
        } else {
          this.setStatus(this.t('Error', 'Sprachsuche nicht moeglich.'));
        }
      }

      onEnd() {
        this.listening = false;
        this.setActive(false);
      }

      setActive(active) {
        this.classList.toggle('hdc-speech--listening', active);
        if (!this.button) return;
        this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
        const label = active
          ? this.t('Stop', 'Sprachsuche beenden')
          : this.t('Start', 'Sprachsuche starten');
        this.button.setAttribute('aria-label', label);
        this.button.setAttribute('title', label);
      }

      setStatus(text) {
        if (this.statusEl) this.statusEl.textContent = text || '';
      }
    }
  );
}
